import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Tags, Plus, Search, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/dashboard/Sidebar";

const EMPTY = { name: "", description: "", default_unit: "each", default_price: "", active: true };

const ServicesPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/services?include_inactive=true`);
      setServices(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return services;
    return services.filter((s) =>
      [s.name, s.description, s.default_unit].some((v) => (v || "").toLowerCase().includes(t)),
    );
  }, [services, search]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      name: s.name || "",
      description: s.description || "",
      default_unit: s.default_unit || "each",
      default_price: s.default_price ?? "",
      active: s.active !== false,
    });
    setDialogOpen(true);
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Service name is required"); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description || "",
      default_unit: form.default_unit || "each",
      default_price: parseFloat(form.default_price) || 0,
      active: !!form.active,
    };
    try {
      if (editing) {
        await axios.put(`${API}/services/${editing}`, payload);
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/services`, payload);
        toast.success("Service added");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone. Existing quotes keep their saved prices.`)) return;
    try {
      await axios.delete(`${API}/services/${s.id}`);
      toast.success("Service deleted");
      load();
    } catch {
      toast.error("Failed to delete service");
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-50">
      <Sidebar onLogout={() => { logout(); navigate("/login"); }} />
      <main className="flex-1 p-6 md:p-10 max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl uppercase tracking-tight text-[#00341d] flex items-center gap-2">
              <Tags className="w-6 h-6" /> Services &amp; Pricing
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Edit the services and base prices that appear in the quote builder. Changes apply to newly added line items only — existing quotes keep their saved prices.
            </p>
          </div>
          <Button onClick={openNew} data-testid="add-service-button" className="bg-[#00341d] hover:bg-[#00341d]/90">
            <Plus className="w-4 h-4 mr-1.5" /> Add Service
          </Button>
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search services…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="services-search-input"
          />
        </div>

        <Card className="border border-zinc-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-zinc-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-zinc-400" data-testid="services-empty">
                <Tags className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No services yet. Use <span className="font-medium">Add Service</span> to create one.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Default Unit</TableHead>
                    <TableHead className="text-right">Base Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                      <TableCell>
                        <div className="font-medium text-[#00341d]">{s.name}</div>
                        {s.description ? (
                          <div className="text-xs text-zinc-400 line-clamp-1 max-w-md">{s.description}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-zinc-600">{s.default_unit}</TableCell>
                      <TableCell className="text-right font-medium">
                        ${(s.default_price ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${s.active !== false ? "bg-green-100 text-green-700" : "bg-zinc-200 text-zinc-500"}`}>
                          {s.active !== false ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`} className="h-8 w-8 p-0">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`delete-service-${s.id}`} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Service" : "Add Service"}</DialogTitle>
            <DialogDescription>
              Base pricing and defaults used when this service is added to a quote.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-zinc-700">Name</label>
              <Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="service-name-input" placeholder="e.g. Forestry Mulching" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setF("description", e.target.value)}
                data-testid="service-description-input"
                rows={4}
                className="w-full mt-1 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00341d]/20"
                placeholder="Scope details shown on the quote line item"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700">Default Unit</label>
                <Input value={form.default_unit} onChange={(e) => setF("default_unit", e.target.value)} data-testid="service-unit-input" placeholder="acre / day / mile / each" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700">Base Price ($)</label>
                <Input type="number" step="0.01" min="0" value={form.default_price} onChange={(e) => setF("default_price", e.target.value)} data-testid="service-price-input" placeholder="0.00" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setF("active", e.target.checked)} data-testid="service-active-checkbox" className="rounded border-zinc-300" />
              <span>Active (show in the quote builder)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="service-cancel-button">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="service-save-button" className="bg-[#00341d] hover:bg-[#00341d]/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save Changes" : "Add Service")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesPage;
